import { useAxios } from '@/features/common/helpers/axios';
import { parseError } from '@/features/common/helpers/errorParser';
import { GlobalResponseType } from '@/features/common/types';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { isLocalhost } from '@/features/common/helpers';
import { routesAvailableWithoutAuth } from '@/features/auth/helpers';
import {
  ConfirmationCodeEnum, SendConfirmationCodeResponse, SignInData, TokenData, legacyAuthState,
} from '../types';

export const { axiosService, baseURL } = useAxios();
export const { axiosService: axiosServiceV2, baseURL: baseURLV2 } = useAxios(2);

export const { axiosService: axiosServiceRoot, baseURL: rootBaseURL } = useAxios(1, '', true);

export interface VerifyEmailResponse {
  isConfirmed: boolean,
  username: string,
  tokenModel: TokenData
}

export async function getBrowserHash(): Promise<string> {
  const fp = await FingerprintJS.load();
  const { visitorId } = await fp.get();
  return visitorId;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parsePasswordUpdateError(err: any) {
  let errorMessage = '';

  if (err.response?.data?.errors && err.response.data.errors.NewPasswordConfirmation) {
    errorMessage = err.response.data.errors.NewPasswordConfirmation[0] || '';
  }

  if (err.response?.data?.errors && err.response.data.errors.NewPassword) {
    errorMessage = err.response.data.errors.NewPassword[0] || '';
  }

  const { errorMessage: parsedErrorMessage } = parseError(err);

  return {
    successful: false,
    errorMessage: errorMessage || parsedErrorMessage,
    redirectUrl: '',
  };
}

export const signInUser = async (
  data: SignInData,
): Promise<{ tokenData?: TokenData } & GlobalResponseType> => {
  try {
    const response = await axiosService.post('/auth/sign-in', data, {
      headers: {
        Integrity: await getBrowserHash(),
      },
    });
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      tokenData: response.data,
    };
  } catch (err) {
    return parseError(err);
  }
};

export const fetchConfirmStatus = async (
  email: string,
  confirmationCodeType: ConfirmationCodeEnum,
): Promise<{ data?: SendConfirmationCodeResponse } & GlobalResponseType> => {
  try {
    const response = await axiosServiceV2.post(
      `/auth/confirmation/status/${confirmationCodeType}`,
      {
        email,
      },
    );
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      data: response.data,
    };
  } catch (err) {
    return parseError(err);
  }
};

export const validateEmailConfirmationCode = async (
  { email, code }: { email: string; code: string },
  confirmationCodeType: ConfirmationCodeEnum,
): Promise<{ tokenData?: TokenData } & GlobalResponseType> => {
  try {
    const response = await axiosServiceV2.post(
      `/auth/confirmation/validate/${confirmationCodeType}`,
      {
        email,
        code,
        Integrity: await getBrowserHash(),
      },
    );
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      tokenData: response.data,
    };
  } catch (err) {
    return parseError(err);
  }
};

export const sendEmailConfirmationCode = async (
  email: string,
  confirmationCodeType: ConfirmationCodeEnum,
): Promise<GlobalResponseType> => {
  try {
    await axiosServiceV2.post(
      `/auth/confirmation/resend/${confirmationCodeType}`,
      { email },
    );
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
    };
  } catch (err) {
    return parseError(err);
  }
};

export const saveTokenOnLegacySite = async (
  data: TokenData,
): Promise<GlobalResponseType> => {
  try {
    await axiosServiceRoot.post('/webship/rates/commonwebmethods.aspx/SetJwt', {
      jwtToken: data,
    });
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
    };
  } catch (err) {
    return parseError(err);
  }
};

export const checkLegacyAuthStatus = async (): Promise<boolean> => {
  if (isLocalhost()
  || routesAvailableWithoutAuth.some((route) => window.location.href.includes(route))) return true;

  try {
    await axiosServiceRoot.post<null, { data: legacyAuthState }>(
      '/webship/login.aspx/hello',
      {},
      { headers: { 'Content-Type': 'application/json' } },
    );
    return true;
  } catch (err) {
    return false;
  }
};

export const verifyEmail = async (
  id: string,
  key: string,
): Promise<VerifyEmailResponse | GlobalResponseType> => {
  try {
    const res = await axiosService.get(`/users/verify-email/${id}/${key}`);
    return res.data;
  } catch (er) {
    return parseError(er);
  }
};

export const changePassword = async (
  newPassword: string,
  newPasswordConfirmation: string,
  isNewUser = false,
): Promise<GlobalResponseType> => {
  try {
    const url = isNewUser ? '/auth/set-password' : '/auth/change-password';

    await axiosService.post(url, {
      newPassword,
      newPasswordConfirmation,
    });

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
    };
  } catch (err) {
    return parsePasswordUpdateError(err);
  }
};

export const verifyPasswordTokenService = async (
  id: string,
  key: string,
): Promise<{ data?: null } & GlobalResponseType> => {
  try {
    await axiosService.get(`/auth/password/${id}/${key}/verify-token`);
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
    };
  } catch (er) {
    return parseError(er);
  }
};

export const resetPasswordWithTokenService = async (
  newPassword: string,
  newPasswordConfirmation: string,
  id:string,
  key: string,
): Promise<{data?: TokenData} & GlobalResponseType> => {
  try {
    const res = await axiosService.post(`/auth/password/${id}/${key}/reset`, {
      newPassword,
      newPasswordConfirmation,
    }, {
      headers: {
        Integrity: await getBrowserHash(),
      },
    });
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      data: res.data,
    };
  } catch (err) {
    return parsePasswordUpdateError(err);
  }
};

export const verifyAndAddNewUserToAccountService = async (
  newPassword: string,
  newPasswordConfirmation: string,
  id:string,
  key: string,
): Promise<{data?: TokenData} & GlobalResponseType> => {
  try {
    const res = await axiosServiceV2.post(`/users/verify-and-add-new-user-to-account/${id}/${key}`, {
      newPassword,
      newPasswordConfirmation,
    }, {
      headers: {
        Integrity: await getBrowserHash(),
      },
    });
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      data: res.data,
    };
  } catch (err) {
    return parsePasswordUpdateError(err);
  }
};
